import {
  Checkbox,
  ColorPicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { Form, Formik, FormikProps } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  ClassInput,
  useCreateClassMutation,
  useUpdateClassesMutation,
} from '../../../types/planung-graphql-client-defs';
import { colorPickerOptions, UsedColors } from '../../../utils/colorPickerOptions';
import { blockIfEventJobRunning, createEvents } from '../../../utils/eventsApi';
import { naturalStringSort } from '../../../utils/stringHelper';
import { showSuccessCreateToast, showUserDbErrorToast } from '../../../utils/toast';
import { ClassesFormType } from '../types/ClassesTypes';
import { validationSchema } from './ClassesForm.validation';
import { ClassesFormNameField } from './ClassesFormNameField';

type ClassesFormProps = {
  classUuid: string | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const ClassesForm = observer(({ classUuid, onLoad, onClose }: ClassesFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const classesContext = useMemorizedCacheTag('CLASSES');
  const currentSchoolYear = useSelectedSchoolYear();

  const [, createClass] = useCreateClassMutation();
  const [, updateClass] = useUpdateClassesMutation();

  const { classesData, teacherData, roomsData, gradeGroupData } = useLoadBasicData({ pause: false });

  const currentClass = classUuid ? classesData?.classes.find((c) => c.uuid === classUuid) : null;

  const colorsInUse: Array<UsedColors | null> =
    classesData?.classes?.map((c) => {
      return {
        color: c.timetableConfig?.color,
        uuid: c.uuid,
        owner: {
          __typename: 'Class',
          name: c.name,
        },
      };
    }) ?? [];

  const colorPickerData = colorPickerOptions({
    htmlColorsInUse: colorsInUse,
    currentColor: currentClass?.timetableConfig?.color ?? '',
    currentColorUsedBy: currentClass?.name,
    forTypename: 'Class',
  });

  const roomOptions = useCreateSelectOptions(roomsData?.rooms, 'uuid', 'name');
  const gradeGroupsOptions = useCreateSelectOptions(gradeGroupData?.gradeGroups, 'uuid', 'name');
  const tutorsOptions = useCreateSelectOptions(
    teacherData?.people.sort((a, b) => naturalStringSort(a.selectName, b.selectName)),
    'uuid',
    'selectName',
  );

  const gradeOptions = useMemo(
    () =>
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, null].map((grade) => {
        return {
          value: grade,
          label: grade === null ? t('functionClass.title', { count: 1 }) : grade.toString(),
        };
      }),
    [t],
  );

  const initialValues: ClassesFormType = {
    uuid: currentClass?.uuid ?? '',
    name: currentClass?.name ?? '',
    shortName: currentClass?.shortName ?? '',
    certificatesEnabled: currentClass?.certificatesEnabled ?? false,
    relevantForSubstitution: currentClass?.relevantForSubstitution ?? false,
    grade:
      currentClass?.grade === null || !!currentClass?.grade || currentClass?.grade === 0
        ? currentClass?.grade
        : undefined,
    gradeGroupUuid: currentClass?.gradeGroup?.uuid ?? null,
    tutorUuid_1: currentClass?.tutorUuids[0] ?? '',
    tutorUuid_2: currentClass?.tutorUuids[1] ?? '',
    tutorUuid_3: currentClass?.tutorUuids[2] ?? '',
    color: currentClass?.timetableConfig?.color ?? '',
    colorLabel: currentClass?.timetableConfig?.colorLabel ?? '',
    printOrder: currentClass?.timetableConfig?.printOrder ?? undefined,
    printPageNr: currentClass?.timetableConfig?.printPageNr ?? undefined,
    roomUuid: currentClass?.defaultRoomUuid ?? '',
    studentCount: currentClass?.studentCount ?? null,
  };

  if (!currentClass) {
    initialValues.certificatesEnabled = false;
    initialValues.relevantForSubstitution = true;
    initialValues.grade = undefined;
    initialValues.gradeGroupUuid = null;
    initialValues.tutorUuid_1 = '';
    initialValues.tutorUuid_2 = '';
    initialValues.tutorUuid_3 = '';
    initialValues.color = '';
    initialValues.colorLabel = '';
    initialValues.printOrder = undefined;
    initialValues.printPageNr = undefined;
    initialValues.roomUuid = '';
    initialValues.studentCount = null;
  }

  const handleSubmit = async (values: ClassesFormType, formHelpers: FormikHelpers<ClassesFormType>) => {
    // `relevantForSubstitution` speist relevantForEventsClasses() im Backend und entscheidet damit,
    // ob für die Klasse überhaupt Events entstehen. Das Update allein legt aber keine an — ohne
    // angestoßenen EventJob bliebe der Vertretungsplan bis zum nächtlichen Lauf leer.
    const substitutionRelevanceChanged =
      !!classUuid && values.relevantForSubstitution !== (currentClass?.relevantForSubstitution ?? false);

    if (substitutionRelevanceChanged && (await blockIfEventJobRunning())) {
      return;
    }

    onLoad(true);

    // Das Backend erwartet immer den vollständigen Stand; Klassenraum, Klassenzug und Klassenlehrer werden ersetzt.
    const input: ClassInput = {
      shortName: values.shortName,
      name: values.name,
      grade: values.grade,
      certificatesEnabled: values.certificatesEnabled,
      relevantForSubstitution: values.relevantForSubstitution,
      studentCount: values.studentCount && !isNaN(Number(values.studentCount)) ? Number(values.studentCount) : 0,
      defaultRoomUuid: values.roomUuid || null,
      gradeGroupUuid: values.gradeGroupUuid || null,
      tutorUuids: [values.tutorUuid_1, values.tutorUuid_2, values.tutorUuid_3].filter((uuid): uuid is string => !!uuid),
      color: values.color || null,
      colorLabel: values.colorLabel || null,
      printOrder: Number(values.printOrder),
      printPageNr: Number(values.printPageNr),
    };

    if (classUuid) {
      const { error } = await updateClass({ uuid: classUuid, input }, classesContext);

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
        if (substitutionRelevanceChanged && currentSchoolYear) {
          await createEvents({
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            schoolYearUuid: currentSchoolYear.uuid,
          });
        }
      }
    } else {
      const { error } = await createClass({ schoolYearUuid: currentSchoolYear?.uuid ?? '', input }, classesContext);

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
        values,
        handleBlur,
        handleChange,
        resetForm,
        isValidating,
        dirty,
      }: FormikProps<ClassesFormType>) => (
        <Form>
          <Grid useFormGap>
            <GridRow spacingBottom='s'>
              <GridColumn width={10}>
                <GridRow spacingBottom='s'>
                  <GridColumn width={3}>
                    <Select
                      name='grade'
                      value={gradeOptions.find((g) => g.value === values.grade)}
                      options={gradeOptions}
                      label={t('grades.title', { count: 1 })}
                      onChange={async (option) => {
                        const opt = option as SingleValue<SelectOptionType>;
                        await setFieldValue('grade', opt?.value);
                        await setFieldTouched('grade');
                      }}
                      error={errors.grade ? errors.grade : undefined}
                    />
                  </GridColumn>
                  <GridColumn width={3}>
                    <Select
                      name='gradeGroup'
                      value={gradeGroupsOptions.find((gg) => gg.value === values.gradeGroupUuid)}
                      options={gradeGroupsOptions}
                      isClearable
                      isSearchable
                      label={t('gradeGroups.title', { count: 1 })}
                      onChange={async (option) => {
                        const opt = option as SingleValue<SelectOptionType>;
                        await setFieldTouched('gradeGroupUuid', true);
                        await setFieldValue('gradeGroupUuid', opt?.value);
                      }}
                      onBlur={handleBlur}
                    />
                  </GridColumn>
                  <GridColumn width={3}>
                    <ClassesFormNameField
                      onBlur={handleBlur}
                      onChange={handleChange}
                      name='name'
                      label={t('common.name')}
                      longName
                      gradeGroupsOptions={gradeGroupsOptions}
                      showGrade={values.grade || values.grade === 0 ? values.grade >= 0 : false}
                      error={touched.name && errors.name ? errors.name : undefined}
                    />
                  </GridColumn>
                  <GridColumn width={3}>
                    <ClassesFormNameField
                      onBlur={handleBlur}
                      onChange={handleChange}
                      name='shortName'
                      label={t('common.shortName')}
                      gradeGroupsOptions={gradeGroupsOptions}
                      showGrade={values.grade || values.grade === 0 ? values.grade >= 0 : false}
                      error={touched.shortName && errors.shortName ? errors.shortName : undefined}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <Select
                    isClearable
                    isSearchable
                    name='tutorUuid_1'
                    options={tutorsOptions.filter(
                      (t) => ![values.tutorUuid_2, values.tutorUuid_3].includes(t.value as string),
                    )}
                    label={t('classes.tutor.full') + ' 1'}
                    value={tutorsOptions.find((tutor) => tutor.value === values.tutorUuid_1)}
                    onChange={async (option) => {
                      const opt = option as SingleValue<SelectOptionType>;
                      await setFieldValue('tutorUuid_1', opt?.value);
                      await setFieldTouched('tutorUuid_1');
                    }}
                    onBlur={handleBlur}
                  />
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <Select
                    isClearable
                    isSearchable
                    name='tutorUuid_2'
                    options={tutorsOptions.filter(
                      (t) => ![values.tutorUuid_1, values.tutorUuid_3].includes(t.value as string),
                    )}
                    label={t('classes.tutor.full') + ' 2'}
                    value={tutorsOptions.find((tutor) => tutor.value === values.tutorUuid_2)}
                    onChange={async (option) => {
                      const opt = option as SingleValue<SelectOptionType>;
                      await setFieldValue('tutorUuid_2', opt?.value);
                      await setFieldTouched('tutorUuid_2');
                    }}
                    onBlur={handleBlur}
                    disabled={!values.tutorUuid_1}
                  />
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <Select
                    isClearable
                    isSearchable
                    name='tutorUuid_3'
                    options={tutorsOptions.filter(
                      (t) => ![values.tutorUuid_1, values.tutorUuid_2].includes(t.value as string),
                    )}
                    label={t('classes.tutor.full') + ' 3'}
                    value={tutorsOptions.find((tutor) => tutor.value === values.tutorUuid_3)}
                    onChange={async (option) => {
                      const opt = option as SingleValue<SelectOptionType>;
                      await setFieldValue('tutorUuid_3', opt?.value);
                      await setFieldTouched('tutorUuid_3');
                    }}
                    onBlur={handleBlur}
                    disabled={!values.tutorUuid_2}
                  />
                </GridRow>
                <GridRow spacingBottom='s' spacingTop='s'>
                  <GridColumn width={8}>
                    <Select
                      isSearchable
                      label={t('rooms.standard')}
                      isClearable
                      options={roomOptions}
                      name='room'
                      value={roomOptions.find((opt) => opt.value === values.roomUuid)}
                      onChange={async (option) => {
                        const opt = option as SingleValue<SelectOptionType>;
                        await setFieldTouched(`roomUuid`, true);
                        await setFieldValue(`roomUuid`, opt?.value);
                      }}
                    />
                  </GridColumn>
                  <GridColumn width={4}>
                    <Input
                      name='studentCount'
                      label={t('classes.studentCount.full')}
                      tooltipText={t('classes.studentCount.tooltip')}
                      placeholder='0'
                      value={values.studentCount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.studentCount && errors.studentCount ? errors.studentCount : undefined}
                    />
                  </GridColumn>
                </GridRow>
              </GridColumn>
              <GridColumn width={2}>
                <Checkbox
                  className='mt-3'
                  name='certificatesEnabled'
                  label={t('classes.certificatesEnabled')}
                  onChange={handleChange}
                  checked={values.certificatesEnabled}
                />
                <Checkbox
                  className='mt-3'
                  name='relevantForSubstitution'
                  label={t('classes.relevantForSubstitution')}
                  onChange={handleChange}
                  checked={values.relevantForSubstitution}
                  tooltipText={t('classes.relevantForSubstitutionTooltip')}
                />
              </GridColumn>
            </GridRow>

            <GridRow headline={t('timetable.title', { count: 2 })}>
              <GridColumn width={3}>
                <Input
                  name='printPageNr'
                  label={t('timetable.printPageNr')}
                  value={values.printPageNr}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.printPageNr && errors.printPageNr ? errors.printPageNr : undefined}
                />
              </GridColumn>
              <GridColumn width={3}>
                <Input
                  name='printOrder'
                  label={t('timetable.printOrder')}
                  value={values.printOrder}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.printOrder && errors.printOrder ? errors.printOrder : undefined}
                />
              </GridColumn>
              <GridColumn width={4} offset='right'>
                <ColorPicker
                  name='color'
                  label={t('common.color')}
                  options={colorPickerData.groupedColorOptions}
                  maxMenuHeight={300}
                  menuPosition='fixed'
                  onChange={async (option) => {
                    await setFieldValue('color', option?.html);
                    await setFieldValue('colorLabel', option?.label);
                    await setFieldTouched('color');
                  }}
                  onBlur={handleBlur}
                  defaultValue={colorPickerData.currentColor}
                />
              </GridColumn>
            </GridRow>
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            submitButton={{ disabled: !dirty || isValidating || Object.keys(errors).length > 0 }}
          />
        </Form>
      )}
    </Formik>
  );
});

ClassesForm.displayName = 'ClassesForm';
